import React, { useEffect, useRef, useState } from "react";
import { ImageUp, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Immagine di contenuto sostituibile dall'admin ("Cambia foto").
 * Regola: le foto NON vengono mai tagliate → object-fit / background-size: contain, centrate.
 * props:
 *  - imageKey: identificatore stabile lato server (es. "smart_hospitality_image")
 *  - defaultSrc: immagine statica di fallback
 *  - as: "img" (default) | "background"
 *  - className: applicato al contenitore (default "relative")
 *  - style: stile aggiuntivo del contenitore
 *  - imgClassName: classi dell'<img> (default contain, 100%)
 *  - alt, children (children solo per as="background", es. overlay/testo)
 *  - selfGroup: se false, l'hover è gestito da un ".group" esterno
 */
export const AdminEditableImage = ({
  imageKey, defaultSrc, as = "img", className = "relative", style = {},
  alt = "", imgClassName = "", children = null, selfGroup = true,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [src, setSrc] = useState(defaultSrc);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setSrc(defaultSrc); // reagisce SUBITO al nuovo slug/imageKey (evita hero "stale" tra categorie)
    api.get(`/site-images`).then(({ data }) => {
      const url = data?.images?.[imageKey];
      if (alive && url) setSrc(`${BACKEND}${url}`);
    }).catch(() => {});
    return () => { alive = false; };
  }, [imageKey, defaultSrc]);

  const pick = (e) => { e.stopPropagation(); e.preventDefault(); inputRef.current?.click(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const prev = src;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/admin/site-images/${imageKey}`, fd); // upload = salvataggio automatico
      setSrc(`${BACKEND}${data.url}${data.url.includes("?") ? "&" : "?"}_t=${Date.now()}`);
      toast.success("Immagine aggiornata");
    } catch (err) {
      setSrc(prev); // mantiene l'immagine precedente in caso di errore
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito");
    } finally { setBusy(false); }
  };

  const overlay = isAdmin && (
    <>
      <button type="button" data-testid={`admin-edit-image-${imageKey}`} onClick={pick} disabled={busy}
        className="absolute top-2 left-2 z-20 inline-flex items-center gap-1.5 rounded-full bg-black/70 text-white text-xs font-semibold px-3 py-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity backdrop-blur-sm hover:bg-black/85">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageUp className="h-3.5 w-3.5" />}
        {busy ? "Caricamento…" : "Cambia foto"}
      </button>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
    </>
  );

  const wrapCls = `${selfGroup ? "group " : ""}${className}`;

  if (as === "background") {
    return (
      <div className={wrapCls}
        style={{ backgroundColor: "#ffffff", ...style, backgroundImage: `url(${src})`,
                 backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }}>
        {overlay}
        {children}
      </div>
    );
  }
  return (
    <div className={wrapCls} style={style}>
      <img src={src} alt={alt} className={imgClassName || "w-full h-full object-contain"} />
      {overlay}
    </div>
  );
};

export default AdminEditableImage;
